import { Outlet, createFileRoute, useRouterState } from '@tanstack/react-router'
import { PageHeader } from '../components/ui/index.ts'
import { VaultCeremonyPrompt } from '../components/VaultCeremonyPrompt.tsx'
import { VaultIdleWarning } from '../components/VaultIdleWarning.tsx'
import { VaultUnlockBanner } from '../components/VaultUnlockBanner.tsx'
import { withSessionRedirect } from '../lib/require-session.ts'
import { fetchHierarchy } from '../lib/hierarchy-api.ts'
import { requireTenantProduct } from '../lib/tenant-products.ts'
import { getTenantKey } from '../lib/vault-api.ts'

export const Route = createFileRoute('/vault')({
  loader: async ({ context }) =>
    withSessionRedirect(async () => {
      // Entitlement first, for the whole /vault subtree: every vault page
      // is a child of this layout, so one guard here covers Projects,
      // Principals, Approvals, Daemons, Audit and Tenant key. A tenant
      // without the Vault product is refused all of them by the API with
      // a 403, and this sends them to the landing page rather than
      // rendering six pages of permission errors.
      await requireTenantProduct(context.queryClient, 'vault')
      const tree = await fetchHierarchy()
      const rootId = tree.roots[0]?.id ?? ''
      const tenantKey = rootId ? await getTenantKey(rootId) : null
      return { rootId, tenantKey }
    }),
  component: VaultLayout,
})

/**
 * The vault layout is now chrome-free.
 *
 * It used to render an `<h1>Vault</h1>` and its own six-link sub-nav, both
 * of which the app shell has taken over: the Vault section of `lib/nav.ts`
 * IS this sub-nav, and every vault page already renders its own `<h1>`
 * (Approvals, Daemons, Audit, Principals & Grants, Tenant key). Keeping
 * both meant two `<h1>`s on every vault page — the page's real one, under
 * a generic "Vault" — which breaks heading navigation for exactly the
 * people it is supposed to help.
 *
 * The sidebar's links are still client-side `<Link>`s. The reason the old
 * sub-nav existed has softened since ADR 0009 — a browser that has been
 * told to remember the key now re-adopts it after a full page load — but
 * it has not gone: one that has NOT been told still loses the key on a
 * reload and is asked for the recovery file again, which is exactly what
 * was found live on 2026-09-05.
 *
 * What is left here is what is genuinely shared by the vault pages and
 * nothing else: the three tenant-key surfaces, and the `<Outlet />`.
 *
 * All three live at this level rather than on the pages that trigger them
 * because the key is held per browser, not per page. A recovery-file
 * confirmation raised by Approve on /vault/daemons and an idle warning
 * raised by a timer that started on /vault/audit are the same operator's
 * business wherever they happen to be standing, and a prompt that only
 * renders on the page that caused it is a prompt that misses the case it
 * was written for.
 */
function VaultLayout() {
  const { rootId, tenantKey } = Route.useLoaderData()
  // There is no `/vault` index route, so a bare `/vault` renders this
  // layout around an empty outlet. The sidebar never links there, but the
  // URL is typeable and used to at least show a heading; this keeps the
  // one-`<h1>`-per-page rule true for it too, instead of serving a page
  // with no heading and nothing in it.
  const isSectionRoot =
    useRouterState({ select: (state) => state.location.pathname }) === '/vault'

  return (
    // A `<div>`, not a `<main>` — `AppShell` owns the one `<main>` on the
    // page now, and nesting a second one inside it is invalid and gives
    // assistive tech two "main" landmarks to choose between.
    <div className="p-8">
      {isSectionRoot && (
        <PageHeader
          title="Vault"
          description="Pick a vault page from the sidebar."
        />
      )}
      {/* Order is deliberate and is a priority order, not a layout
          accident. The ceremony prompt is first because it is the only
          one the operator has to answer RIGHT NOW — it is a modal, and
          an operation is blocked behind it. The idle warning is next:
          sixty seconds, and then the key is gone. The unlock banner is
          last because it describes a resting state that will wait, and
          on a device that remembers the key it renders nothing at
          all. */}
      <VaultCeremonyPrompt />
      <VaultIdleWarning />
      <VaultUnlockBanner
        rootId={rootId}
        tenantPubkey={tenantKey?.tenantPubkey ?? null}
      />
      <Outlet />
    </div>
  )
}
